import { useCallback, useState } from 'react';
import { useQuery, type QueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { CLOSED_LEAD_STATUSES } from '@/lib/follow-ups';

export interface AdminInboxCounts {
  /** Customer booking requests still waiting for approval (today onwards). */
  bookingRequests: number;
  /** Website enquiries that nobody has picked up yet. */
  newEnquiries: number;
  /** Quotes the customer accepted online that haven't been booked in yet. */
  acceptedQuotes: number;
  /** Open leads (enquiries + callbacks) whose follow-up date is today or past. */
  followUpsDue: number;
  /** Ids of the outstanding requests + enquiries (capped), used for seen tracking. */
  itemIds: string[];
}

export const ADMIN_INBOX_COUNTS_KEY = 'admin-inbox-counts';

/** Ids fetched per source for seen tracking; counts stay exact beyond this. */
const ID_LIMIT = 200;

/** Won or lost leads never need a follow-up (kept in step with src/lib/follow-ups). */
const CLOSED_STATUS_LIST = [...CLOSED_LEAD_STATUSES].join(',');

/** Call after any change to job requests or enquiries so the bell/sidebar update immediately. */
export function invalidateAdminInboxCounts(queryClient: QueryClient) {
  return queryClient.invalidateQueries({ queryKey: [ADMIN_INBOX_COUNTS_KEY] });
}

/**
 * Cheap counts for the admin top-bar notifications bell. Owners and
 * supervisors only (cleaners never see requests/enquiries). Errors resolve to
 * zero so the shell never breaks on a failed count.
 */
export function useAdminInboxCounts(orgId: string | null | undefined, enabled: boolean) {
  return useQuery({
    queryKey: [ADMIN_INBOX_COUNTS_KEY, orgId],
    enabled: Boolean(orgId) && enabled,
    staleTime: 60_000,
    queryFn: async (): Promise<AdminInboxCounts> => {
      const today = format(new Date(), 'yyyy-MM-dd');
      const [requests, enquiries, quotes, followUps, callbackFollowUps] = await Promise.all([
        supabase
          .from('jobs')
          .select('id', { count: 'exact' })
          .eq('organization_id', orgId!)
          .eq('status', 'pending_confirmation')
          .gte('scheduled_date', today)
          .limit(ID_LIMIT),
        supabase
          .from('enquiries')
          .select('id', { count: 'exact' })
          .eq('organization_id', orgId!)
          .eq('status', 'pending')
          .limit(ID_LIMIT),
        // quotes isn't in the generated types yet (see useQuotes).
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (supabase as any)
          .from('quotes')
          .select('id', { count: 'exact' })
          .eq('organization_id', orgId!)
          .eq('status', 'accepted')
          .is('job_id', null)
          .limit(ID_LIMIT) as Promise<{ data: { id: string }[] | null; count: number | null; error: unknown }>,
        supabase
          .from('enquiries')
          .select('id, follow_up_on', { count: 'exact' })
          .eq('organization_id', orgId!)
          .lte('follow_up_on', today)
          .not('status', 'in', `(${CLOSED_STATUS_LIST})`)
          .limit(ID_LIMIT),
        supabase
          .from('callback_requests')
          .select('id, follow_up_on', { count: 'exact' })
          .eq('organization_id', orgId!)
          .lte('follow_up_on', today)
          .not('status', 'in', `(${CLOSED_STATUS_LIST})`)
          .limit(ID_LIMIT),
      ]);
      // A follow-up is a fresh alert each time its date is (re)set, so its seen
      // id carries the date — and never collides with the enquiry's own id.
      const followUpIds = [
        ...(followUps.error ? [] : followUps.data ?? []),
        ...(callbackFollowUps.error ? [] : callbackFollowUps.data ?? []),
      ].map((row) => `follow-up:${row.id}:${row.follow_up_on}`);
      return {
        bookingRequests: requests.error ? 0 : requests.count ?? 0,
        newEnquiries: enquiries.error ? 0 : enquiries.count ?? 0,
        acceptedQuotes: quotes.error ? 0 : quotes.count ?? 0,
        followUpsDue:
          (followUps.error ? 0 : followUps.count ?? 0) + (callbackFollowUps.error ? 0 : callbackFollowUps.count ?? 0),
        itemIds: [
          ...(requests.error ? [] : requests.data ?? []),
          ...(enquiries.error ? [] : enquiries.data ?? []),
          ...(quotes.error ? [] : quotes.data ?? []),
        ].map((row) => row.id).concat(followUpIds),
      };
    },
  });
}

const seenStorageKey = (userId: string, orgId: string) => `adm-bell-seen:${userId}:${orgId}`;

function readSeen(key: string | null): Set<string> {
  if (!key) return new Set();
  try {
    const raw = window.localStorage.getItem(key);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : []);
  } catch {
    return new Set();
  }
}

/**
 * "Read" state for the bell badge. The bell lists outstanding work (pending
 * requests/enquiries) which only leaves the list once actioned, so the badge
 * instead counts items the user hasn't seen yet. Opening the bell marks the
 * current items as seen; new arrivals bring the badge back. Persisted per
 * user + org in localStorage so it survives reloads.
 */
export function useAdminInboxSeen(
  userId: string | null | undefined,
  orgId: string | null | undefined,
  counts: AdminInboxCounts | undefined,
) {
  const key = userId && orgId ? seenStorageKey(userId, orgId) : null;
  const [seen, setSeen] = useState(() => ({ key, ids: readSeen(key) }));
  // Re-read when the user/org changes (adjust-state-during-render pattern).
  if (seen.key !== key) setSeen({ key, ids: readSeen(key) });
  const seenIds = seen.key === key ? seen.ids : readSeen(key);

  const total =
    (counts?.bookingRequests ?? 0) + (counts?.newEnquiries ?? 0) + (counts?.acceptedQuotes ?? 0) + (counts?.followUpsDue ?? 0);
  const seenCount = counts ? counts.itemIds.filter((id) => seenIds.has(id)).length : 0;
  const unseenCount = Math.max(0, total - seenCount);

  const itemIds = counts?.itemIds;
  const markSeen = useCallback(() => {
    if (!key || !itemIds) return;
    // Store only what's outstanding now, so the list never grows unbounded.
    const next = new Set(itemIds);
    try {
      window.localStorage.setItem(key, JSON.stringify([...next]));
    } catch {
      // Storage unavailable (private mode / quota) — still clear for this session.
    }
    setSeen({ key, ids: next });
  }, [key, itemIds]);

  return { unseenCount, markSeen };
}
